import {DndProvider} from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import {ProjectIssueOverrideTag} from "common/types/project";
import {
  IssueOverrideTag,
  IssueOverrideTagId,
} from "common/types/issue_override_tag";
import React, {useState, useEffect, useMemo} from "react";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import Autocomplete, {createFilterOptions} from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import IssueOverrideTagItem from "./issue_override_tag_item";

interface IssueOverrideTagsTabProps {
  projectIssueOverrideTags: ProjectIssueOverrideTag[];
  issueOverrideTags: IssueOverrideTag[];
  onAddTag: (name: string, tagId?: IssueOverrideTagId) => void;
  onRemoveTag: (tagId: IssueOverrideTagId) => void;
  onReorderTag: (tagId: IssueOverrideTagId, newOrder: number) => Promise<void>;
}

interface TagOption {
  id: IssueOverrideTagId;
  name: string;
}

interface CreateNewOption {
  inputValue: string;
  name: string;
}

type Option = TagOption | CreateNewOption;

const styles: Record<string, React.CSSProperties> = {
  container: {
    boxSizing: "border-box",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    padding: "1rem",
  },
  textContainer: {
    backgroundColor: "#f5f5f5",
    padding: "1rem",
    borderRadius: "4px",
    lineHeight: "1.5",
    color: "#555",
    borderLeft: "4px solid #2196f3",
    fontWeight: 400,
  },
};

const isCreateNewOption = (option: Option): option is CreateNewOption => {
  return "inputValue" in option;
};

const IssueOverrideTagsTab: React.FC<IssueOverrideTagsTabProps> = ({
  projectIssueOverrideTags,
  issueOverrideTags,
  onAddTag,
  onRemoveTag,
  onReorderTag,
}) => {
  const orderedTags = useMemo(
    () => [...projectIssueOverrideTags].sort((a, b) => a.order - b.order),
    [projectIssueOverrideTags],
  );

  const [displayedTags, setDisplayedTags] = useState<ProjectIssueOverrideTag[]>(
    orderedTags,
  );
  const [selectedValue, setSelectedValue] = useState<Option>();
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    setDisplayedTags(orderedTags);
  }, [orderedTags]);

  const availableTags = useMemo(
    () =>
      issueOverrideTags
        .filter(tag => !projectIssueOverrideTags.some(pt => pt.id === tag.id))
        .map(tag => ({
          id: tag.id,
          name: tag.name,
        })),
    [issueOverrideTags, projectIssueOverrideTags],
  );

  const handleChange = (
    _event: React.ChangeEvent,
    newValue: string | Option,
    reason: string,
  ) => {
    if (!newValue || reason === "clear") {
      return;
    }

    if (typeof newValue === "string") {
      onAddTag(newValue);
    } else if (isCreateNewOption(newValue)) {
      onAddTag(newValue.inputValue);
    } else {
      onAddTag(newValue.name, newValue.id);
    }
  };

  const handleInputChange = (
    _event: React.ChangeEvent,
    newInputValue: string,
    reason: string,
  ) => {
    if (reason === "reset") {
      setInputValue("");
      setSelectedValue(undefined);
      return;
    }
    setInputValue(newInputValue);
  };

  const handleClose = () => {
    setSelectedValue(undefined);
    setInputValue("");
  };

  const getOptionLabel = (option: string | Option) =>
    typeof option === "string"
      ? option
      : isCreateNewOption(option)
      ? option.inputValue
      : option.name;

  const renderOption = (option: Option) =>
    isCreateNewOption(option) ? `Add "${option.inputValue}"` : option.name;

  const filterOptions = (options: TagOption[], params): Option[] => {
    const filtered = createFilterOptions<TagOption>()(
      options,
      params,
    ) as Option[];
    const inputValue = params.inputValue.trim();

    if (
      inputValue &&
      !options.some(
        option => option.name.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      filtered.push({
        inputValue,
        name: `Add "${inputValue}"`,
      });
    }

    return filtered;
  };

  const handleDragMove = (dragIndex: number, hoverIndex: number) => {
    setDisplayedTags(prevItems => {
      const newItems = [...prevItems];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(hoverIndex, 0, draggedItem);
      return newItems;
    });
  };

  const handleDrop = async (dragId: number, newIndex: number) => {
    try {
      await onReorderTag(dragId, newIndex + 1);
    } catch (error) {
      setDisplayedTags(orderedTags);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={styles.container}>
        <Typography variant="h6">Issue Override Tags</Typography>
        <Typography variant="body1" style={styles.textContainer}>
          Create new tags or add existing ones from other projects. Tags can be
          assigned overrides for specific checklist issue fields via the
          Playbook. When multiple tag overrides apply to an issue, field display
          values will be shown in the order set here using drag and drop.
        </Typography>
        <Autocomplete
          selectOnFocus
          clearOnBlur
          blurOnSelect
          handleHomeEndKeys
          freeSolo
          options={availableTags}
          value={selectedValue}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onClose={handleClose}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          renderInput={params => (
            <TextField
              {...params}
              label="Add tag"
              variant="outlined"
              helperText="Select an existing tag or type to create a new one"
              style={{width: "100%"}}
            />
          )}
          filterOptions={filterOptions}
        />

        <List style={{width: "100%", padding: 0}}>
          {displayedTags.map((tag, index) => (
            <IssueOverrideTagItem
              key={tag.id}
              id={tag.id}
              name={tag.name}
              index={index}
              onRemove={onRemoveTag}
              handleDragMove={handleDragMove}
              handleDrop={handleDrop}
            />
          ))}
        </List>

        {!projectIssueOverrideTags.length && (
          <Typography
            color="textSecondary"
            style={{textAlign: "center", marginBottom: "0.5rem"}}
          >
            No tags added yet
          </Typography>
        )}
      </div>
    </DndProvider>
  );
};

export default IssueOverrideTagsTab;
